import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Calculator, ChevronLeft, ChevronRight } from 'lucide-react';
import { api, type UurloonScenario } from '../lib/api';
import { cn, formatDate, formatMoney } from '../lib/utils';
import { ICON, IconButton, Sheet } from './ui';

const STANDAARD = [18, 19, 20];
/** Loonperiodes per jaar (4 weken). */
const PERIODES_PER_JAAR = 13;

const euro0 = (n: number) => formatMoney(Math.round(n)).replace(/,00$/, '');
const plus = (n: number | null) => (n == null ? '–' : `${n >= 0 ? '+' : '−'}${formatMoney(Math.abs(n))}`);

/** Knop in de bovenbalk + paneel: wat levert deze periode op bij een ander uurloon? */
export function SalarisSimulatie({ compact }: { compact?: boolean }) {
  const [open, setOpen] = useState(false);
  const [eigen, setEigen] = useState('');
  // Gekozen periode; null = de huidige.
  const [keuze, setKeuze] = useState<{ year: number; period: number } | null>(null);
  const extra = Number(eigen.replace(',', '.'));
  const uurlonen = [...STANDAARD, ...(extra > 0 && extra < 100 && !STANDAARD.includes(extra) ? [extra] : [])];
  const { data, isFetching, error } = useQuery({
    queryKey: ['simulatie', uurlonen.join(','), keuze?.year, keuze?.period],
    queryFn: () => api.dashboard.simulatie(uurlonen, keuze?.year, keuze?.period),
    enabled: open,
    placeholderData: keepPreviousData,
    staleTime: 5 * 60_000,
  });

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          'inline-flex h-8 items-center gap-1.5 rounded-full border border-surface-line px-3 text-xs font-medium text-slate-300 transition-colors duration-fast ease-calm hover:border-slate-500 hover:text-slate-100',
          compact && 'w-8 justify-center px-0'
        )}
        aria-label="Salaris simuleren"
      >
        <Calculator className={ICON.sm} aria-hidden />
        {!compact && 'Simuleren'}
      </button>

      <Sheet
        open={open}
        onClose={() => setOpen(false)}
        title="Wat als je uurloon anders was?"
        sub={data ? `${formatDate(data.start_date)} t/m ${formatDate(data.end_date)}, ${String(data.uren).replace('.', ',')} uur` : undefined}
      >
        <div className="mb-4 flex items-center justify-between gap-2 rounded-lg border border-surface-line p-1">
          <IconButton
            icon={ChevronLeft}
            label="Vorige periode"
            onClick={() => data && setKeuze(data.period === 1 ? { year: data.year - 1, period: 13 } : { year: data.year, period: data.period - 1 })}
          />
          <div className="min-w-0 text-center">
            <div className="text-sm font-medium text-slate-100">{data ? `Periode ${data.period}, ${data.year}` : 'Periode'}</div>
            {keuze && (
              <button type="button" className="text-xs text-slate-400 hover:text-slate-100" onClick={() => setKeuze(null)}>
                Naar huidige periode
              </button>
            )}
          </div>
          <IconButton
            icon={ChevronRight}
            label="Volgende periode"
            onClick={() => data && setKeuze(data.period === 13 ? { year: data.year + 1, period: 1 } : { year: data.year, period: data.period + 1 })}
          />
        </div>

        <p className="t-muted">
          Dezelfde diensten en toeslagen, alleen een ander uurloon. Toeslagen zijn een percentage van je uurloon en stijgen dus mee.
          Netto met dezelfde berekening als de rest van de app.
        </p>

        {error ? (
          <p className="mt-4 text-sm text-red-300">Voor deze periode is er nog geen rooster of kalender.</p>
        ) : !data ? (
          <p className="mt-4 text-sm text-slate-500">{isFetching ? 'Rekenen…' : ''}</p>
        ) : (
          <div className={cn('mt-4 space-y-2 transition-opacity duration-fast', isFetching && 'opacity-60')}>
            {data.uren === 0 && <p className="text-sm text-slate-400">In deze periode staan (nog) geen diensten.</p>}
            <Scenario s={data.huidig} label="Jouw uurloon" huidig />
            {data.scenarios.map((s) => (
              <Scenario key={s.uurloon} s={s} />
            ))}
          </div>
        )}

        <div className="mt-5">
          <label className="label" htmlFor="sim-uurloon">Ander uurloon proberen</label>
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-400">€</span>
            <input
              id="sim-uurloon"
              inputMode="decimal"
              placeholder="bijv. 21,50"
              className="input w-32"
              value={eigen}
              onChange={(e) => setEigen(e.target.value)}
            />
            <span className="text-sm text-slate-400">per uur</span>
          </div>
        </div>

        <p className="mt-5 text-xs text-slate-500">
          Bedragen per regel: bruto / netto. Zelfde totaal als het overzicht, inclusief een uitkering als die in deze periode valt.
          Per jaar = {PERIODES_PER_JAAR} × het loon van deze periode + eindejaarsuitkering + vakantiegeld van dat jaar, zonder reiskosten.
        </p>
      </Sheet>
    </>
  );
}

function Scenario({ s, label, huidig }: { s: UurloonScenario; label?: string; huidig?: boolean }) {
  return (
    <div className={cn('rounded-lg border p-3', huidig ? 'border-surface-line bg-surface' : 'border-surface-line')}>
      <div className="flex items-baseline justify-between gap-3">
        <span className="font-semibold text-slate-50">
          {formatMoney(s.uurloon)} <span className="text-sm font-normal text-slate-400">per uur{label ? `, ${label.toLowerCase()}` : ''}</span>
        </span>
        {!huidig && <span className="t-num text-sm font-medium text-money">{plus(s.verschil_netto)} netto</span>}
      </div>
      <dl className="mt-2 grid grid-cols-3 gap-2 text-xs">
        <div>
          <dt className="text-slate-400">Bruto periode</dt>
          <dd className="t-num mt-0.5 text-sm text-slate-100">{formatMoney(s.bruto)}</dd>
        </div>
        <div>
          <dt className="text-slate-400">Netto periode</dt>
          <dd className="t-num mt-0.5 text-sm text-slate-100">{s.netto != null ? formatMoney(s.netto) : '–'}</dd>
        </div>
        <div>
          <dt className="text-slate-400">Netto per jaar</dt>
          <dd className="t-num mt-0.5 text-sm text-slate-100">{s.jaar.netto != null ? euro0(s.jaar.netto) : '–'}</dd>
        </div>
      </dl>
      <dl className="mt-2 space-y-0.5 border-t border-surface-line/70 pt-2 text-xs">
        {s.posten.map((p) => (
          <div key={p.key} className="flex justify-between gap-3">
            <dt className="text-slate-400">{p.label}</dt>
            <dd className="t-num text-slate-300">
              {formatMoney(p.bruto)} <span className="text-slate-500">/ {p.netto != null ? formatMoney(p.netto) : '–'}</span>
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
